import type { ReferralResponse, ReferralUpdate } from '@lmc/validations'
import { message, type UploadFile } from 'antd'
import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'
import { REFERRALS_CATEGORY_ID } from '../constants'

type UpdateReferralInput = ReferralUpdate & { _id: string }
type Referral = ReferralResponse

interface UseReferralUpdateReturn {
  updateReferral: (
    input: UpdateReferralInput,
    fileList?: UploadFile[]
  ) => Promise<Referral>
  isUpdating: boolean
}

export const useReferralUpdate = (
  residentId: string,
  onSuccess?: () => void
): UseReferralUpdateReturn => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      input,
      fileList
    }: {
      input: UpdateReferralInput
      fileList?: UploadFile[]
    }) => {
      const { _id, ...updateData } = input

      if (!_id) {
        throw new Error('Referral ID is required for update')
      }

      const hasFiles = fileList && fileList.length > 0

      let attachmentIds: string[] | undefined

      // Upload new documents using presign/commit flow if files are provided
      if (hasFiles) {
        try {
          // Fetch current referral to get existing attachments
          const response = await getJSON({
            url: `/api/list/referrals/${_id}`
          })
          const currentReferral = response.result

          // Get existing attachment IDs
          const existingAttachmentIds = currentReferral?.attachments
            ? currentReferral.attachments.map((att: any) => att._id || att.id)
            : []

          // Upload new documents
          const newAttachmentIds = await uploadDocuments(
            fileList,
            residentId,
            REFERRALS_CATEGORY_ID
          )

          // Combine existing and new attachment IDs
          attachmentIds = [...existingAttachmentIds, ...newAttachmentIds]
        } catch (error) {
          throw new Error('Failed to upload attachments')
        }
      }

      const formattedUpdateData = {
        ...updateData,
        // Include attachments field if new files were uploaded (merged with existing)
        ...(attachmentIds !== undefined && { attachments: attachmentIds })
      }

      const response = await postJSON({
        url: `/api/list/referrals/${_id}`,
        method: 'PUT',
        json: formattedUpdateData
      })

      return response.result as Referral
    },
    {
      onSuccess: () => {
        message.success('Referral updated successfully')
        // Invalidate referrals list to refetch
        queryClient.invalidateQueries(['referrals', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update referral')
      }
    }
  )

  const updateReferral = async (
    input: UpdateReferralInput,
    fileList?: UploadFile[]
  ): Promise<Referral> => {
    return mutation.mutateAsync({ input, fileList })
  }

  return {
    updateReferral,
    isUpdating: mutation.isLoading
  }
}
